import React from 'react';
import PropTypes from 'prop-types';
import { Droppable, Draggable } from 'react-beautiful-dnd';

import './emails.css';
import {
  PALETTE_GROUPS, BLOCK_BLURBS, labelFor, paletteDraggableId, paletteDroppableId,
} from '../../../utils/emailBlockDoc';

// The block palette, in the shape this CMS's page builder already uses
// (Views/DragAndDrop/DragAndDropContainer.jsx): one `isDropDisabled` Droppable
// per group, each holding Draggables that are copied into the canvas rather
// than moved out of it. Users of this admin have been dragging modules out of
// that column for years; this is deliberately the same gesture.
//
// It is NOT that file's 830 lines. There are six block types, they never
// change at runtime, and nothing here fetches anything — so no search box, no
// collapsing sections, no per-group state.
//
// Two ways in, because the CMS palette has always had two: drag a tile to a
// position, or click it to append. Clicking is what people do when the
// document is empty and there is nowhere to drop yet.
//
// The tile itself is the drag handle (`dragHandleProps` on the outer div), and
// the "Add" button inside it is a real <button> so the palette is reachable by
// keyboard without depending on rbd's own keyboard sensor. Both call the same
// onAdd.

// react-beautiful-dnd fixes a dragged item's position against the viewport. The
// admin's left column scrolls and is inside a CSS grid, so the tile is styled
// only while dragging, and only enough to see it under the cursor.
const tileStyle = (isDragging, draggableStyle) => ({
  ...draggableStyle,
  ...(isDragging ? { boxShadow: '0 6px 18px rgba(0,0,0,0.18)' } : null),
});

const ICONS = {
  heading: 'H',
  text: '¶',
  button: '▭',
  image: '▣',
  divider: '—',
  spacer: '↕',
};

const BlockPalette = ({ onAdd }) => (
  <div className="blockpalette">
    {PALETTE_GROUPS.map(group => (
      <div className="palettegroup" key={group.key}>
        <div className="palettegroup-label">{group.label}</div>
        <Droppable droppableId={paletteDroppableId(group.key)} isDropDisabled>
          {provided => (
            <div className="palettelist" ref={provided.innerRef} {...provided.droppableProps}>
              {group.types.map((type, index) => (
                <Draggable key={type} draggableId={paletteDraggableId(type)} index={index}>
                  {(dragProvided, snapshot) => (
                    <div
                      className={snapshot.isDragging ? 'paletteitem is-dragging' : 'paletteitem'}
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      style={tileStyle(snapshot.isDragging, dragProvided.draggableProps.style)}
                      title={`${labelFor(type)} — drag into the email, or click to add it at the end`}
                      onClick={() => onAdd(type)}
                    >
                      <span className="paletteicon" aria-hidden="true">{ICONS[type]}</span>
                      <span className="palettetext">
                        <b>{labelFor(type)}</b>
                        <i>{BLOCK_BLURBS[type]}</i>
                      </span>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    ))}
    <p className="hint palettehint">
      Drag a block into the email, or click it to add it at the end.
    </p>
  </div>
);

BlockPalette.propTypes = {
  onAdd: PropTypes.func.isRequired,
};

export default BlockPalette;
